import { useEffect, useState } from 'react';
import { Box, Button, Chip, Paper, TextField, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  useAdminPatchLoanApplicationMutation,
  useAdminSeedLoanChecklistMutation,
  useGetLoanApplicationQuery,
} from '@/apis/extended/loanApplicationApi';
import { LoanFileAdminControls } from '@/components/Admin/AdminWorkspace/LoanFileAdminControls';
import { LoanApplicationShell } from '@/components/CommercialLoanApplication/LoanApplicationShell';

const AdminLoanApplicationDetailPage = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const { data: app, isLoading } = useGetLoanApplicationQuery(applicationId!, {
    skip: !applicationId,
  });
  const [patchAdmin, { isLoading: saving }] =
    useAdminPatchLoanApplicationMutation();
  const [seedChecklist, { isLoading: seeding }] =
    useAdminSeedLoanChecklistMutation();

  const [internalNotes, setInternalNotes] = useState('');

  useEffect(() => {
    if (app) setInternalNotes(app.internalNotes || '');
  }, [app]);

  if (!applicationId) {
    navigate('/admin/loan-pipeline');
    return null;
  }

  if (isLoading || !app) {
    return <Typography sx={{ p: 2 }}>Loading…</Typography>;
  }

  const saveMeta = async () => {
    try {
      await patchAdmin({ id: app._id, internalNotes }).unwrap();
      toast.success('Saved');
    } catch {
      toast.error('Save failed');
    }
  };

  const canToggleBorrowerEdits =
    app.status === 'submitted' || app.status === 'in_review';

  const unlockBorrowerEdits = async () => {
    try {
      await patchAdmin({
        id: app._id,
        borrowerEditsUnlocked: true,
      }).unwrap();
      toast.success('Borrower can edit this application again');
    } catch {
      toast.error('Update failed');
    }
  };

  const lockBorrowerEdits = async () => {
    try {
      await patchAdmin({
        id: app._id,
        borrowerEditsUnlocked: false,
      }).unwrap();
      toast.success('Borrower editing turned off');
    } catch {
      toast.error('Update failed');
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Loan file
      </Typography>
      <Button sx={{ mb: 2 }} onClick={() => navigate('/admin/loan-pipeline')}>
        Back to pipeline
      </Button>

      {app.auditLog && app.auditLog.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Activity
          </Typography>
          <Box
            component="ul"
            sx={{ m: 0, pl: 2, maxHeight: 200, overflow: 'auto' }}
          >
            {app.auditLog
              .slice()
              .reverse()
              .slice(0, 30)
              .map((a, i) => (
                <li key={i}>
                  <Typography variant="caption" component="span">
                    {a.at ? new Date(a.at).toLocaleString() : '—'} — {a.action}{' '}
                    {a.details ? `(${a.details})` : ''}
                  </Typography>
                </li>
              ))}
          </Box>
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Operations
        </Typography>
        {canToggleBorrowerEdits ? (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Borrower editing
            </Typography>
            <Chip
              size="small"
              color={app.borrowerEditsUnlocked ? 'success' : 'default'}
              label={app.borrowerEditsUnlocked ? 'Unlocked' : 'Locked'}
            />
          </Box>
        ) : null}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
          }}
        >
          <Box sx={{ gridColumn: { md: '1 / -1' } }}>
            <LoanFileAdminControls app={app} />
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              gridColumn: { md: '1 / -1' },
            }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate(`/admin/user/${app.applicantUserId}`)}
            >
              Open borrower profile
            </Button>
            {canToggleBorrowerEdits && !app.borrowerEditsUnlocked ? (
              <Button
                variant="contained"
                color="secondary"
                onClick={() => void unlockBorrowerEdits()}
                disabled={saving}
              >
                Allow borrower to edit
              </Button>
            ) : null}
            {canToggleBorrowerEdits && app.borrowerEditsUnlocked ? (
              <Button
                variant="outlined"
                color="warning"
                onClick={() => void lockBorrowerEdits()}
                disabled={saving}
              >
                Turn off borrower editing
              </Button>
            ) : null}
            <Button
              variant="outlined"
              onClick={async () => {
                try {
                  await seedChecklist(app._id).unwrap();
                  toast.success('Checklist reset to template');
                } catch {
                  toast.error('Failed');
                }
              }}
              disabled={seeding}
            >
              Reset checklist
            </Button>
          </Box>
          <TextField
            label="Internal notes (not visible to borrower)"
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            sx={{ gridColumn: { md: '1 / -1' } }}
          />
          <Box>
            <Button variant="contained" onClick={saveMeta} disabled={saving}>
              Save notes
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Full application (editable for admin)
        </Typography>
        <LoanApplicationShell
          applicationId={applicationId}
          variant="embedded"
        />
      </Paper>
    </Box>
  );
};

export { AdminLoanApplicationDetailPage };
